import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import type { Photo, GalleryResponse, OverallUploadProgress, UploadResult } from '../types';

interface UseGalleryOptions {
  initialPage?: number;
  limit?: number;
  order?: 'asc' | 'desc';
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function useGallery(options: UseGalleryOptions = {}) {
  const { initialPage = 1, limit = 50, order = 'desc', autoRefresh = false, refreshInterval = 30000 } = options;

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [pagination, setPagination] = useState<GalleryResponse['pagination']>({
    page: initialPage,
    limit,
    total: 0,
    totalPages: 0,
  });
  const [albumName, setAlbumName] = useState('Das ganze Wochenende');
  const [privacyNotice, setPrivacyNotice] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const loadPhotos = useCallback(async (page: number, append = false) => {
    try {
      setError(null);
      if (!append) setIsLoading(true);

      const data = await api.photos.getGallery(page, limit, order);
      setAlbumName(data.albumName);
      setPrivacyNotice(data.privacyNotice);

      if (append) {
        setPhotos(prev => [...prev, ...data.photos]);
      } else {
        setPhotos(data.photos);
      }
      setPagination(data.pagination);
      setHasMore(page < data.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Galerie');
    } finally {
      setIsLoading(false);
    }
  }, [limit, order]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      loadPhotos(pagination.page + 1, true);
    }
  }, [isLoading, hasMore, pagination.page, loadPhotos]);

  const refresh = useCallback(() => {
    loadPhotos(1, false);
  }, [loadPhotos]);

  useEffect(() => {
    loadPhotos(initialPage, false);
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (!isLoading) {
        loadPhotos(1, false);
      }
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, isLoading, loadPhotos]);

  return {
    photos,
    pagination,
    albumName,
    privacyNotice,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

export function usePhotoCount() {
  const [count, setCount] = useState(0);
  const [storageUsed, setStorageUsed] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await api.photos.getCount();
      setCount(data.count);
      setStorageUsed(data.storageUsed);
    } catch (err) {
      console.error('Failed to load photo count:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { count, storageUsed, isLoading, refresh: load };
}

export function useUpload() {
  const [progress, setProgress] = useState<OverallUploadProgress>({
    totalFiles: 0,
    completedFiles: 0,
    failedFiles: 0,
    totalBytes: 0,
    uploadedBytes: 0,
    overallPercentage: 0,
    isAborted: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const upload = useCallback(async (
    files: File[],
    displayName?: string,
    onComplete?: (results: UploadResult[]) => void
  ): Promise<UploadResult[]> => {
    if (files.length === 0) return [];

    setIsUploading(true);
    setError(null);
    abortControllerRef.current = new AbortController();

    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    setProgress({
      totalFiles: files.length,
      completedFiles: 0,
      failedFiles: 0,
      totalBytes,
      uploadedBytes: 0,
      overallPercentage: 0,
      isAborted: false,
    });

    try {
      const { uploadDirect } = await import('../utils/api-direct');
      const results = await uploadDirect(files, displayName, (overallPct, fileIndex, filePct) => {
        setProgress(prev => ({
          ...prev,
          uploadedBytes: Math.round((overallPct / 100) * totalBytes),
          overallPercentage: overallPct,
          currentFile: {
            fileIndex,
            fileName: files[fileIndex]?.name || '',
            loaded: Math.round((filePct / 100) * (files[fileIndex]?.size || 0)),
            total: files[fileIndex]?.size || 0,
            percentage: filePct,
          },
        }));
      }, abortControllerRef.current?.signal);
      const ok = results.filter((r) => r.success).length;
      setProgress(prev => ({ ...prev, completedFiles: ok, failedFiles: results.length - ok, uploadedBytes: totalBytes, overallPercentage: 100 }));
      onComplete?.(results);
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      setError(message);
      throw err;
    } finally {
      setIsUploading(false);
      setProgress(prev => ({ ...prev, isAborted: abortControllerRef.current?.signal.aborted ?? false }));
    }
  }, []);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setProgress(prev => ({ ...prev, isAborted: true }));
  }, []);

  return {
    progress,
    isUploading,
    error,
    upload,
    abort,
  };
}

export function useDownload() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const downloadSelected = useCallback(async (photoIds: number[]) => {
    if (photoIds.length === 0) return;
    setIsDownloading(true);
    setError(null);
    setProgress(0);
    try {
      const response = await api.photos.download(photoIds);
      const { photos } = response;
      const { downloadAsZips } = await import('../utils/zip');
      if (photos.length === 1) {
        const p = photos[0];
        const r = await fetch(p.url, { credentials: 'include' });
        if (!r.ok) throw new Error(`Download fehlgeschlagen: ${p.filename}`);
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = p.filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setProgress(100);
      } else {
        await downloadAsZips(photos, 'wochenende-2026-auswahl', (p) => setProgress(p.percent));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
      throw err;
    } finally {
      setIsDownloading(false);
      setProgress(0);
    }
  }, []);

  const downloadAll = useCallback(async () => {
    setIsDownloading(true);
    setError(null);
    setProgress(0);
    try {
      const response = await api.photos.downloadAll();
      const { photos } = response;
      const { downloadAsZips } = await import('../utils/zip');
      await downloadAsZips(photos, 'wochenende-2026-alle-fotos', (p) => setProgress(p.percent));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
      throw err;
    } finally {
      setIsDownloading(false);
      setProgress(0);
    }
  }, []);

  return { isDownloading, progress, error, downloadSelected, downloadAll };
}